import { useMemo, useEffect } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';
import { generateAnchor } from '@/lib/markdown-utils';
import { Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import './manual-content.css';

interface ManualContentProps {
  content: string;
  manualId: string;
  className?: string;
  onScrollSpy?: (anchor: string) => void;
}

export function ManualContent({ content, manualId, className, onScrollSpy }: ManualContentProps) {
  const { toast } = useToast();

  // Scroll spy to detect active section
  useEffect(() => {
    if (!onScrollSpy) return;

    // Find the scrollable container
    const container = document.getElementById('manual-scroll-container');
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            if (id) onScrollSpy(id);
          }
        });
      },
      { 
        root: container, // Observe within the container, not the window
        rootMargin: '-20% 0px -80% 0px', 
        threshold: 0 
      }
    );

    const headings = container.querySelectorAll('h2[id], h3[id]');
    headings.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [onScrollSpy, content]);

  const copyLinkToSection = (anchor: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('manual', manualId);
    url.searchParams.set('section', anchor);
    
    navigator.clipboard.writeText(url.toString());
    toast({
      title: "Link copiado",
      description: "Link da seção copiado para a área de transferência",
    });
  };

  // Configure marked options
  marked.setOptions({
    gfm: true,
    breaks: true,
  });

  // Parse markdown to HTML with custom heading renderer
  const htmlContent = useMemo(() => {
    const renderer = new marked.Renderer();
    
    // Custom heading renderer with anchors and copy buttons
    renderer.heading = ({ text, depth }: any) => {
      const anchor = generateAnchor(text);
      return `
        <h${depth} id="${anchor}" class="group flex items-center gap-2 scroll-mt-24">
          ${text}
          <button 
            class="copy-section-link opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center h-6 w-6 p-0 rounded-md hover:bg-muted"
            data-anchor="${anchor}"
            aria-label="Copiar link da seção"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </button>
        </h${depth}>
      `;
    };

    // Custom table renderer with responsive wrapper
    renderer.table = (token: any) => {
      return `
        <div class="overflow-x-auto">
          <table>
            <thead>${token.header}</thead>
            <tbody>${token.body}</tbody>
          </table>
        </div>
      `;
    };

    const parsed = marked.parse(content, { renderer }) as string;
    return DOMPurify.sanitize(parsed);
  }, [content]);

  // Add click handlers for copy buttons
  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const button = target.closest('.copy-section-link') as HTMLButtonElement;
    if (button) {
      e.preventDefault();
      const anchor = button.dataset.anchor;
      if (anchor) {
        copyLinkToSection(anchor);
      }
    }
  };

  return (
    <div 
      className={cn(
        "prose prose-sm dark:prose-invert max-w-none",
        // Espaçamentos e tipografia profissional
        "prose-headings:font-bold prose-headings:tracking-tight",
        "prose-h1:text-3xl prose-h1:mb-6 prose-h1:mt-8 prose-h1:pb-3 prose-h1:border-b",
        "prose-h2:text-2xl prose-h2:mb-4 prose-h2:mt-8",
        "prose-h3:text-xl prose-h3:mb-3 prose-h3:mt-6",
        "prose-p:mb-4 prose-p:leading-relaxed prose-p:text-muted-foreground",
        "prose-ul:my-4 prose-ul:space-y-2",
        "prose-ol:my-4 prose-ol:space-y-2",
        "prose-li:my-1",
        "prose-blockquote:my-4 prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic",
        "prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono",
        "prose-pre:my-4 prose-pre:bg-muted prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto",
        "prose-table:my-6",
        "prose-img:rounded-lg prose-img:shadow-md prose-img:my-6",
        "prose-hr:my-8 prose-hr:border-muted",
        "prose-strong:font-semibold prose-strong:text-foreground",
        className
      )}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
      onClick={handleClick}
    />
  );
}
